import type { Drawing, DrawingEvent, Stroke } from '../shared/art/types';
import type { Platform } from './platform';

type Storage = Pick<Platform, 'get' | 'set' | 'remove'>;
type PackedStroke = Omit<Stroke, 'points'> & { points: number[] };
type PackedEvent = Omit<DrawingEvent, 'stroke' | 'mirrorStroke'> & { stroke?: number; mirrorStroke?: number };
interface PackedDraft { version: 1; background?: string; pool: PackedStroke[]; strokes: number[]; events?: PackedEvent[] }
interface DraftIndex { version: 1; generation: string; parts: number }
// Leave ample room for JSON escaping and UTF-8 under WeChat's per-key limit.
export const DRAFT_CHUNK_SIZE = 200_000;
let generation = 0;
const indexKey = (key: string) => `drawing-draft:${key}`;
const partKey = (key: string, index: DraftIndex, part: number) => `${indexKey(key)}:${index.generation}:${part}`;

/** Local format only: exact coordinates/settings and the complete replay survive packing. */
export function encodeDraft(drawing: Drawing): string {
  const pool: PackedStroke[] = [], references = new Map<Stroke, number>(), values = new Map<string, number>();
  const ref = (stroke: Stroke) => {
    const existing = references.get(stroke); if (existing !== undefined) return existing;
    const points: number[] = [];
    for (const point of stroke.points) points.push(point.x, point.y, point.t);
    const packed = { ...stroke, points }, value = JSON.stringify(packed);
    let id = values.get(value);
    if (id === undefined) { id = pool.length; pool.push(packed); values.set(value, id); }
    references.set(stroke, id); return id;
  };
  const strokes = drawing.strokes.map(ref);
  const events = drawing.events?.map(({ stroke, mirrorStroke, ...event }) => ({ ...event,
    ...(stroke ? { stroke: ref(stroke) } : {}), ...(mirrorStroke ? { mirrorStroke: ref(mirrorStroke) } : {}),
  }));
  return JSON.stringify({ version: 1, background: drawing.background, pool, strokes, events } satisfies PackedDraft);
}

export function decodeDraft(text: string): Drawing {
  const packed = JSON.parse(text) as PackedDraft;
  if (packed.version !== 1 || !Array.isArray(packed.pool) || !Array.isArray(packed.strokes)) throw new Error('草稿格式不可读');
  const pool: Stroke[] = packed.pool.map(stroke => {
    if (!Array.isArray(stroke.points) || stroke.points.length % 3 || !stroke.points.every(Number.isFinite)) throw new Error('草稿笔迹不可读');
    const points: Stroke['points'] = [];
    for (let i = 0; i < stroke.points.length; i += 3) points.push({ x: stroke.points[i], y: stroke.points[i + 1], t: stroke.points[i + 2] });
    return { ...stroke, points };
  });
  const ref = (id: number) => { if (!Number.isInteger(id) || !pool[id]) throw new Error('草稿笔迹缺失'); return pool[id]; };
  return { schemaVersion: 1, ...(packed.background ? { background: packed.background } : {}), strokes: packed.strokes.map(ref),
    ...(packed.events ? { events: packed.events.map(({ stroke, mirrorStroke, ...event }) => ({ ...event,
      ...(stroke !== undefined ? { stroke: ref(stroke) } : {}), ...(mirrorStroke !== undefined ? { mirrorStroke: ref(mirrorStroke) } : {}),
    })) } : {}),
  };
}

function readIndex(storage: Storage, key: string) {
  const index = storage.get<DraftIndex | null>(indexKey(key), null);
  if (index && (index.version !== 1 || typeof index.generation !== 'string' || !Number.isInteger(index.parts) || index.parts < 1 || index.parts > 1000)) throw new Error('草稿索引不可读');
  return index;
}
function cleanParts(storage: Storage, key: string, index: DraftIndex) {
  for (let i = 0; i < index.parts; i++) { try { storage.remove(partKey(key, index, i)); } catch { /* Saved draft remains valid even if cleanup fails. */ } }
}
export function loadDraft(storage: Storage, key: string): Drawing | null {
  const index = readIndex(storage, key);
  if (!index) return storage.get<Drawing | null>(key, null); // Existing installations migrate on their next save.
  const parts: string[] = [];
  for (let i = 0; i < index.parts; i++) {
    const part = storage.get<string | null>(partKey(key, index, i), null);
    if (typeof part !== 'string') throw new Error('草稿未完整读取，请重新打开画纸');
    parts.push(part);
  }
  return decodeDraft(parts.join(''));
}
export function storeDraft(storage: Storage, key: string, drawing: Drawing) {
  const text = encodeDraft(drawing), previous = readIndex(storage, key);
  const next: DraftIndex = { version: 1, generation: `${Date.now().toString(36)}-${++generation}`, parts: Math.ceil(text.length / DRAFT_CHUNK_SIZE) };
  try {
    for (let i = 0; i < next.parts; i++) storage.set(partKey(key, next, i), text.slice(i * DRAFT_CHUNK_SIZE, (i + 1) * DRAFT_CHUNK_SIZE));
    storage.set(indexKey(key), next); // Publish only after every part is safely stored.
  } catch (error) { cleanParts(storage, key, next); throw error; }
  if (previous) cleanParts(storage, key, previous);
  try { storage.remove(key); } catch { /* Never discard the new draft because legacy cleanup failed. */ }
}
export function removeDraft(storage: Storage, key: string) {
  const previous = readIndex(storage, key);
  storage.remove(indexKey(key));
  if (previous) cleanParts(storage, key, previous);
  storage.remove(key);
}
export function draftFailureMessage(error: unknown) {
  const value = error as { name?: string; message?: string; errMsg?: string } | null;
  const detail = `${value?.name ?? ''} ${value?.message ?? ''} ${value?.errMsg ?? ''}`;
  return /quota|storage.*(?:limit|full)|exceed|maximum|空间不足|存储已满/i.test(detail)
    ? '本地存储空间不足，当前画作仍在，请先保存为图片，暂勿关闭游戏。'
    : '草稿暂未保存，当前画作仍在，请先保存为图片，暂勿关闭游戏。';
}
